/**
 * useAuth — reads the authenticated user profile from sessionStorage.
 *
 * The profile is written by LoginPage on successful authentication.
 * Tokens live in sessionStorage (not localStorage) per CLAUDE.md.
 *
 * This hook is read-only. Mutations (login, logout) are handled by
 * LoginPage and the global Axios 401 interceptor respectively.
 */

import { useState, useCallback } from 'react';
import type { UserProfile } from '../types';

function readProfile(): UserProfile | null {
  const raw = sessionStorage.getItem('user_profile');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserProfile;
  } catch {
    // Corrupted sessionStorage — treat as unauthenticated
    return null;
  }
}

export interface AuthState {
  /** Authenticated user profile, or null if not logged in. */
  user: UserProfile | null;
  /** True when a valid user profile is present in sessionStorage. */
  isAuthenticated: boolean;
  /**
   * Clear sessionStorage and reset local state.
   * The Axios 401 interceptor also calls sessionStorage.clear() globally;
   * this is for explicit user-initiated logout from the AppShell nav.
   */
  logout: () => void;
  /**
   * Re-fetch the live profile from GET /api/v1/auth/me and update both
   * sessionStorage and local state. Picks up admin-side changes (e.g. an
   * agent_level promotion) without requiring re-login. Also stores the fresh
   * access token so token-based API gates see the new level.
   *
   * Returns the refreshed profile, or null if the call failed.
   */
  refreshProfile: () => Promise<UserProfile | null>;
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<UserProfile | null>(readProfile);

  const logout = useCallback(() => {
    sessionStorage.clear();
    setUser(null);
  }, []);

  const refreshProfile = useCallback(async (): Promise<UserProfile | null> => {
    const token = sessionStorage.getItem('access_token');
    if (!token) return null;
    try {
      const res = await fetch('/api/v1/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const json = (await res.json()) as {
        data?: { access_token?: string; user?: UserProfile };
      };
      const data = json.data;
      if (!data?.user) return null;

      if (data.access_token) {
        sessionStorage.setItem('access_token', data.access_token);
      }
      sessionStorage.setItem('user_profile', JSON.stringify(data.user));
      sessionStorage.setItem('user_role', data.user.role);
      if (data.user.language) {
        sessionStorage.setItem('user_language', data.user.language);
      }
      setUser(data.user);
      return data.user;
    } catch {
      // Network error — keep the cached profile rather than logging out.
      return null;
    }
  }, []);

  return {
    user,
    isAuthenticated: user !== null,
    logout,
    refreshProfile,
  };
}
